// Shapes must match @sphtech/web2-ads globalExtensions.d.ts exactly —
// TypeScript requires identical types when Window declarations merge.
declare global {
  interface Window {
    _taboola?: Array<{
      [key: string]: unknown;
    }>;
    taboola_enable?: number | string | boolean;
  }
}

import { TABOOLA_CONFIG } from "@app/constants";
import { loadScript } from "@sphtech/dmg-design-system";
import React, { memo, useEffect } from "react";

/**
 * TaboolaScript Component
 *
 * Loads and initializes Taboola recommendation widgets for Single Page Applications (SPA).
 * Based on Taboola's SPA integration guide: https://developers.taboola.com/web-integrations/docs/spa
 *
 * The loader falls back from cdn.taboola.com to the privacy-compatible
 * static.tblcontent.com domain when the primary domain is blocklisted by
 * privacy-focused browsers/extensions, so private-mode traffic is still
 * monetised. See https://developers.taboola.com/web-integrations/docs/js-tag-to-private-traffic-migration
 *
 * @param url - The canonical URL of the current page
 * @param publisherId - The Taboola publisher ID (publication-specific)
 * @param container - The DOM element ID where the widget will be rendered
 * @param placement - The Taboola placement name for tracking
 *
 * Usage:
 * ```tsx
 * <TaboolaScript
 *   url={`${configSite.address}${storyData.path}`}
 *   publisherId={configTaboola.publisherId}
 *   container="below-article-thumbnails-1"
 *   placement="Below Article Thumbnails-1"
 * />
 * ```
 *
 * Configuration:
 * - Publisher ID: Defined in publication config (configTaboola)
 * - Container / Placement is fixed per placement based on Taboola
 */
const TaboolaScript: React.FC<{
  url: string;
  publisherId: string;
  container: string;
  placement: string;
}> = ({ url, publisherId, container, placement }) => {
  useEffect(() => {
    // Initialize _taboola array if not already present
    window._taboola = window._taboola || [];

    void Promise.all([
      new Promise((resolve) => {
        if (window.taboola_enable !== undefined) {
          resolve(window.taboola_enable);
          return;
        }
        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.origin) return;
          if (event.data === "taboola_enable") {
            resolve(window.taboola_enable);
            window.removeEventListener("message", onMessage);
          }
        };
        window.addEventListener("message", onMessage);
      }),
      loadScript(
        `${TABOOLA_CONFIG.loaderUrl}/${publisherId}/loader.js`,
        true,
      ).catch(() =>
        loadScript(
          `${TABOOLA_CONFIG.loaderPrivacyUrl}/${publisherId}/loader.privacy.js`,
          true,
        ),
      ),
    ]).then(
      ([enabled]) => {
        if (!enabled) {
          return;
        }

        // Article context (once per article)
        window._taboola?.push({
          article: "auto",
          url: url,
        });

        // Widget placement
        window._taboola?.push({
          mode: "thumbnails-base-widget",
          container: container,
          placement: placement,
          target_type: "mix",
        });
      },
      () => {
        // Both cdn.taboola.com and static.tblcontent.com failed to load
        // (e.g. fully blocked network) — skip the widget silently.
      },
    );
  }, [url, publisherId, container, placement]);

  return (
    <div
      id={container}
      style={{
        order: 4,
      }}
    />
  );
};

export default memo(TaboolaScript);
